import { QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { isAxiosError } from 'axios'

import { routeTree } from './routeTree.gen'

/** How often a request that failed for a reason worth retrying is retried. */
const RETRIES = 2

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx is the server's final answer: the request is wrong, not unlucky.
      // Retrying one only delays the error the screen has to show — a customer
      // route opened by a staff account answered 403 four times over before
      // the spinner gave way.
      retry: (failureCount, error) => {
        const status = isAxiosError(error) ? error.response?.status : undefined
        if (status && status >= 400 && status < 500) return false
        return failureCount < RETRIES
      },
    },
  },
})

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }

  /**
   * What a route says about the chrome around it.
   *
   * `bare` is the configurator: a full-height surface that brings its own bar,
   * navigation and footer, and shows what the customer sees — which is none of
   * the shell it happens to be nested under. The shells read this rather than
   * matching on the pathname, so moving or renaming a route cannot silently
   * put a staff rail back over a customer's screen.
   */
  interface StaticDataRouteOption {
    chrome?: 'bare'
  }
}
